import React from 'react'
import { useRoutes, Navigate, RouteObject } from 'react-router-dom'
import { ROUTES, URL_CONST } from 'constants/urls'
import { useAuth, usePermissions } from 'commons/customHooks/useAuth'
import { toast } from 'react-toastify'
import { NDPage } from 'container/NDPage'
import { SCREENS, ACTIONS_SCREENS } from 'commons/utils/permissions'
import { ActionPerm, ScreenPerm } from 'components/Structures/Header/constants'
import { StockReport } from 'container/StockReport'
import Main from 'container/Main'
import Home from 'container/Home'
import CreateOrder from 'container/CreateOrder'
import Error from 'container/Error'
import Login from 'container/Login'
import SalesOrder from 'container/SalesOrder'
import ProductPage from 'container/ProductPage'
import ForgetPassword from 'container/ForgetPassword'
import ValidateCode from 'container/ForgetPassword/validateCode'
import ResetPassword from 'container/ForgetPassword/resetPassword'
import ChangedPassword from 'container/ForgetPassword/changedPassword'
import ManageOrders from 'container/ManageOrders'
import ManageOVDetails from 'container/ManageOVDetails'
import SalesOrderSimulate from 'container/SalesOrderSimulate'
import ExtraVolumePage from 'container/ExtraVolumePage/ExtraVolumePage'
import BackofficeRanges from 'container/BackofficeRanges'
import International from 'container/BatchOrder/International'
import Franchices from 'container/BatchOrder/Franchises'
import LeadTimes from 'container/LeadTimes'
import KeyAccounts from 'container/KeyAccounts'

interface PrivateProps {
  element: React.ReactElement
  screen: ScreenPerm
  action?: ActionPerm
  shouldVerify?: boolean
}

const Private = ({ element, screen, action, shouldVerify = true }: PrivateProps) => {
  const { isAuthenticated } = useAuth()
  const { verifyPermission } = usePermissions()
  if (!isAuthenticated()) return <Navigate to={URL_CONST.LOGIN} />
  if (shouldVerify) {
    if (verifyPermission(screen, action)) {
      return element
    } else {
      toast.warning('Não possui permissão para acessar esta página', {
        toastId: 'unauthorized'
      })
      return <Navigate to={'/error'} />
    }
  }
  return element
}

const {
  app,
  login,
  forgetPassword,
  orders,
  manage,
  nd,
  stock_report,
  extra_volume,
  backoffice
} = ROUTES

const routesObject: RouteObject[] = [
  {
    path: app.base,
    element: <Main />,
    children: [
      {
        index: true,
        element: <Private element={<Home />} screen={SCREENS.HOME} shouldVerify={false} />
      },
      {
        path: login.base,
        children: [
          {
            index: true,
            element: <Login />
          }
        ]
      },
      {
        path: forgetPassword.base,
        children: [
          { index: true, element: <ForgetPassword /> },
          { path: forgetPassword.getLink('validateCode'), element: <ValidateCode /> },
          { path: forgetPassword.getLink('resetPassword'), element: <ResetPassword /> },
          {
            path: forgetPassword.getLink('changedPassword'),
            element: <ChangedPassword />
          }
        ]
      },
      { path: app.getLink('error'), element: <Error /> },
      {
        path: manage.base,
        children: [
          {
            index: true,
            element: <Private element={<ManageOrders />} screen={SCREENS.MANAGE} />
          },
          {
            path: manage.getLink('order'),
            children: [
              { path: manage.getLink('order'), element: <Navigate to={manage.base} /> },
              {
                path: `${manage.base}${manage.children.order.getLink('orderDetail')}`,
                children: [
                  {
                    index: true,
                    element: (
                      <Private
                        element={<SalesOrder />}
                        screen={SCREENS.ORDER}
                        action={ACTIONS_SCREENS.SUMMARY}
                      />
                    )
                  },
                  {
                    path: `${manage.base}${manage.children.order.getLink('orderDetail')}${
                      manage.children.order.children.orderDetail.children.orderMaterial
                        .base
                    }`,
                    children: [
                      {
                        index: true,
                        element: <Private element={<ProductPage />} screen={SCREENS.OV} />
                      }
                    ]
                  }
                ]
              }
            ]
          },
          {
            path: manage.getLink('ov'),
            children: [
              { path: manage.getLink('ov'), element: <Navigate to={manage.base} /> },
              {
                path: `${manage.base}${manage.children.ov.getLink('ovDetail')}`,
                children: [
                  {
                    index: true,
                    element: <Private element={<ManageOVDetails />} screen={SCREENS.OV} />
                  },
                  {
                    path: `${manage.base}${manage.children.ov.getLink('ovDetail')}${
                      manage.children.ov.children.ovDetail.children.justificar.base
                    }`,
                    element: <Private element={<ManageOVDetails />} screen={SCREENS.OV} />
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        path: orders.base,
        children: [
          { path: orders.base, element: <Navigate to={manage.base} /> },
          {
            path: orders.getLink('new'),
            element: (
              <Private
                element={<CreateOrder />}
                screen={SCREENS.ORDER}
                action={ACTIONS_SCREENS.EDIT}
              />
            )
          },
          {
            path: `${orders.base}${orders.children.batchOrders.getLink('international')}`,
            element: (
              <Private
                element={<International />}
                screen={SCREENS.INTERNATIONAL_EDI}
                action={ACTIONS_SCREENS.PROCESS}
              />
            )
          },
          {
            path: `${orders.base}${orders.children.batchOrders.getLink('franchises')}`,
            element: (
              <Private
                element={<Franchices />}
                screen={SCREENS.FRANCHISES_BATCH}
                action={ACTIONS_SCREENS.SEND}
              />
            )
          },
          {
            path: orders.getLink('orderDetail'),
            children: [
              {
                index: true,
                path: orders.getLink('orderDetail'),
                element: <Private element={<SalesOrder />} screen={SCREENS.ORDER} />
              },
              {
                path: `${orders.base}${orders.children.orderDetail.getLink(
                  'orderSimulate'
                )}`,
                element: (
                  <Private
                    element={<SalesOrderSimulate />}
                    screen={SCREENS.ORDER}
                    action={ACTIONS_SCREENS.REQUEST}
                  />
                )
              },
              {
                path: `${orders.base}${orders.children.orderDetail.getLink(
                  'orderMaterials',
                  orders.base
                )}`,
                element: (
                  <Private
                    element={<ProductPage />}
                    screen={SCREENS.ORDER}
                    action={ACTIONS_SCREENS.EDIT}
                  />
                )
              }
            ]
          }
        ]
      },
      {
        path: nd.base,
        children: [
          {
            index: true,
            element: <Private element={<NDPage />} screen={SCREENS.ND_MANAGE} />
          }
        ]
      },
      {
        path: extra_volume.base,
        children: [
          {
            index: true,
            element: (
              <Private element={<ExtraVolumePage />} screen={SCREENS.EXTRA_VOLUME} />
            )
          }
        ]
      },
      {
        path: stock_report.base,
        children: [
          {
            index: true,
            element: (
              <Private
                element={<StockReport />}
                screen={SCREENS.HOME}
                shouldVerify={false}
              />
            )
          }
        ]
      },
      {
        path: backoffice.base,
        children: [
          {
            index: true,
            path: backoffice.base,
            element: <Navigate to={app.base} />
          },
          {
            path: backoffice.getLink('ranges'),
            element: (
              <Private element={<BackofficeRanges />} screen={SCREENS.BACKOFFICE_RANGE} />
            )
          },
          {
            path: backoffice.getLink('lead_times'),
            element: (
              <Private element={<LeadTimes />} screen={SCREENS.TECHNICAL_CONSULTATION} />
            )
          },
          {
            path: backoffice.getLink('key_accounts'),
            element: (
              <Private
                element={<KeyAccounts />}
                screen={SCREENS.TECHNICAL_CONSULTATION}
              />
            )
          }
        ]
      },
      { path: '*', element: <Navigate to="/error" /> }
    ]
  }
]
const Routes = () => useRoutes(routesObject)
export default Routes
